import { useEffect, useState } from 'react';
import { allows } from '../lib/consent';

/*
  LinkedIn Insight Tag — React port of LinkedIn's official snippet.

  Set REACT_APP_LINKEDIN_PARTNER_ID in .env to activate. With no ID, nothing is
  injected, so the site builds and deploys safely without a LinkedIn account.

  Gated on marketing consent: ePrivacy requires prior consent before a tracking
  script may be placed, so this does not load until the visitor accepts.

  What it provides: aggregate Website Demographics (job function, seniority,
  industry, company) and conversion tracking. It does NOT identify individual
  visitors — LinkedIn strips direct identifiers within 7 days.

  Scope note: LinkedIn's terms forbid the tag on pages offering financial or
  health services to CONSUMERS. Our finance pages are B2B, which their
  documentation explicitly permits, so it loads site-wide. Exclude here if a
  consumer-facing page is ever added.
*/

const PARTNER_ID = process.env.REACT_APP_LINKEDIN_PARTNER_ID || '';
const SCRIPT_ID = 'linkedin-insight-tag';
const SCRIPT_SRC = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';

export default function LinkedInInsight() {
  const [granted, setGranted] = useState(() => allows('marketing'));

  // React to the visitor making — or later changing — a cookie decision.
  useEffect(() => {
    const onConsent = () => setGranted(allows('marketing'));
    window.addEventListener('eagentix:consent', onConsent);
    return () => window.removeEventListener('eagentix:consent', onConsent);
  }, []);

  useEffect(() => {
    if (!PARTNER_ID) return;
    if (!granted) return;                            // prior consent required
    if (document.getElementById(SCRIPT_ID)) return;  // StrictMode double-invoke guard

    // Partner id registry, per LinkedIn's snippet.
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    if (!window._linkedin_data_partner_ids.includes(PARTNER_ID)) {
      window._linkedin_data_partner_ids.push(PARTNER_ID);
    }

    // lintrk() must exist and queue calls BEFORE insight.min.js finishes
    // loading, otherwise any conversion fired early is dropped.
    if (!window.lintrk) {
      window.lintrk = function (a, b) { window.lintrk.q.push([a, b]); };
      window.lintrk.q = [];
    }

    const b = document.createElement('script');
    b.id = SCRIPT_ID;
    b.type = 'text/javascript';
    b.async = true;
    b.src = SCRIPT_SRC;
    const s = document.getElementsByTagName('script')[0];
    if (s && s.parentNode) s.parentNode.insertBefore(b, s);
    else document.body.appendChild(b);
  }, [granted]);

  if (!PARTNER_ID || !granted) return null;

  // Fallback pixel — also what LinkedIn's validator looks for.
  return (
    <noscript>
      <img
        height="1"
        width="1"
        style={{ display: 'none' }}
        alt=""
        src={`https://px.ads.linkedin.com/collect/?pid=${PARTNER_ID}&fmt=gif`}
      />
    </noscript>
  );
}

/**
 * Fire a LinkedIn conversion. Safe to call when the tag is absent or consent
 * was refused — it simply no-ops.
 *   trackLinkedInConversion('12345678')   // conversion id from Campaign Manager
 */
export function trackLinkedInConversion(conversionId) {
  if (!conversionId) return;
  if (typeof window === 'undefined' || typeof window.lintrk !== 'function') return;
  try {
    window.lintrk('track', { conversion_id: conversionId });
  } catch (_) {
    /* never break the page for analytics */
  }
}
